/**
 * Voice DSP — pure functions over Float32Array. No Web Audio, no DOM.
 *
 * Reference: research/08-voice-to-pemf-algorithm-design.md
 *
 * Two decisions here are counterintuitive and deliberate:
 *
 * 1. We DOWNSAMPLE to 16 kHz. Higher sample rate is worse for this task.
 *    At 48 kHz with N=4096 the bin width is 11.72 Hz — but one semitone at a
 *    116 Hz fundamental is only ~7 Hz, i.e. 0.6 of a bin. At 16 kHz with
 *    N=16384 the bin width is 0.977 Hz over a 1.024 s window, which actually
 *    resolves pitch.
 *
 * 2. Every measure is reported as a raw value and compared only against the
 *    SAME PERSON on the SAME DEVICE. Published clinical cutoffs are never
 *    shown. That is both the scientifically correct choice (device offsets
 *    make absolute values incomparable) and the regulatory one.
 */

export const TARGET_SAMPLE_RATE = 16000;
export const FFT_SIZE = 16384;

/* ------------------------------------------------------------------ *
 * Resampling
 * ------------------------------------------------------------------ */

/** Linear-interpolation resampler. Adequate here — we analyse well below Nyquist. */
export function resample(input: Float32Array, fromRate: number, toRate: number): Float32Array {
  if (fromRate === toRate) return input;
  const ratio = fromRate / toRate;
  const outLength = Math.floor(input.length / ratio);
  const out = new Float32Array(outLength);
  for (let i = 0; i < outLength; i++) {
    const pos = i * ratio;
    const i0 = Math.floor(pos);
    const i1 = Math.min(i0 + 1, input.length - 1);
    const frac = pos - i0;
    out[i] = input[i0] * (1 - frac) + input[i1] * frac;
  }
  return out;
}

/* ------------------------------------------------------------------ *
 * Level & framing
 * ------------------------------------------------------------------ */

export function rms(frame: Float32Array): number {
  let sum = 0;
  for (let i = 0; i < frame.length; i++) sum += frame[i] * frame[i];
  return Math.sqrt(sum / frame.length);
}


/**
 * Split into voiced frames above a level floor.
 *
 * NOTE: iOS applies automatic gain control that cannot be disabled — the
 * `autoGainControl` constraint is undeclared in WebKit and silently ignored.
 * So the floor is RELATIVE to the loudest frame, never an absolute dBFS value.
 */
export function voicedFrames(
  signal: Float32Array,
  frameSize: number,
  hopSize: number,
  relativeFloorDb = -25,
): { frames: Float32Array[]; offsets: number[] } {
  const frames: Float32Array[] = [];
  const offsets: number[] = [];
  const levels: number[] = [];

  for (let off = 0; off + frameSize <= signal.length; off += hopSize) {
    const frame = signal.subarray(off, off + frameSize);
    levels.push(rms(frame));
    offsets.push(off);
  }
  if (levels.length === 0) return { frames: [], offsets: [] };

  const peak = Math.max(...levels);
  const floor = peak * Math.pow(10, relativeFloorDb / 20);

  const keptOffsets: number[] = [];
  for (let i = 0; i < levels.length; i++) {
    if (levels[i] >= floor) {
      frames.push(signal.slice(offsets[i], offsets[i] + frameSize));
      keptOffsets.push(offsets[i]);
    }
  }
  return { frames, offsets: keptOffsets };
}

/* ------------------------------------------------------------------ *
 * Pitch — YIN
 * ------------------------------------------------------------------ */

export type PitchResult = { hz: number; probability: number };

/**
 * YIN fundamental frequency estimation (de Cheveigné & Kawahara 2002).
 *
 * Chosen over plain autocorrelation because it suppresses the octave errors
 * autocorrelation is prone to, which matter a great deal here — an octave
 * error would move the derived frequency by a factor of two.
 */
export function yinPitch(
  frame: Float32Array,
  sampleRate: number,
  minHz = 60,
  maxHz = 500,
  threshold = 0.15,
): PitchResult {
  const tauMin = Math.floor(sampleRate / maxHz);
  const tauMax = Math.min(Math.floor(sampleRate / minHz), Math.floor(frame.length / 2));
  if (tauMax <= tauMin) return { hz: 0, probability: 0 };

  // Step 1: difference function
  const diff = new Float32Array(tauMax);
  for (let tau = tauMin; tau < tauMax; tau++) {
    let sum = 0;
    for (let i = 0; i < frame.length - tau; i++) {
      const d = frame[i] - frame[i + tau];
      sum += d * d;
    }
    diff[tau] = sum;
  }

  // Step 2: cumulative mean normalised difference
  const cmnd = new Float32Array(tauMax);
  cmnd[tauMin] = 1;
  let running = 0;
  for (let tau = tauMin + 1; tau < tauMax; tau++) {
    running += diff[tau];
    cmnd[tau] = running === 0 ? 1 : (diff[tau] * (tau - tauMin)) / running;
  }

  // Step 3: absolute threshold — first local minimum below threshold
  let tauEstimate = -1;
  for (let tau = tauMin + 1; tau < tauMax - 1; tau++) {
    if (cmnd[tau] < threshold) {
      while (tau + 1 < tauMax - 1 && cmnd[tau + 1] < cmnd[tau]) tau++;
      tauEstimate = tau;
      break;
    }
  }
  if (tauEstimate === -1) {
    // Fall back to the global minimum, but report low confidence.
    let best = tauMin + 1;
    for (let tau = tauMin + 1; tau < tauMax; tau++) if (cmnd[tau] < cmnd[best]) best = tau;
    tauEstimate = best;
    if (cmnd[best] > 0.6) return { hz: 0, probability: 0 };
  }

  // Step 4: parabolic interpolation for sub-sample precision
  const x0 = tauEstimate > tauMin ? tauEstimate - 1 : tauEstimate;
  const x2 = tauEstimate + 1 < tauMax ? tauEstimate + 1 : tauEstimate;
  let betterTau = tauEstimate;
  if (x0 !== tauEstimate && x2 !== tauEstimate) {
    const s0 = cmnd[x0];
    const s1 = cmnd[tauEstimate];
    const s2 = cmnd[x2];
    const denom = 2 * (2 * s1 - s2 - s0);
    if (denom !== 0) betterTau = tauEstimate + (s2 - s0) / denom;
  }

  return {
    hz: sampleRate / betterTau,
    probability: Math.max(0, Math.min(1, 1 - cmnd[tauEstimate])),
  };
}

/* ------------------------------------------------------------------ *
 * Perturbation measures
 * ------------------------------------------------------------------ */

/**
 * Local jitter — mean absolute difference between consecutive periods,
 * divided by the mean period. Expressed as a percentage.
 *
 *   jitter = ( (1/(N-1)) Σ |T_i − T_{i+1}| ) / ( (1/N) Σ T_i ) × 100
 *
 * CAUTION (research/08): jitter moves in the OPPOSITE direction under stress
 * (d = −0.62, Kappen 2024) than it does under dysphonia. Never interpret a
 * rise as "worse". This is why the output is labelled vocal EFFORT, not stress.
 */
export function jitterLocal(periods: number[]): number {
  if (periods.length < 2) return 0;
  let absDiff = 0;
  for (let i = 0; i < periods.length - 1; i++) absDiff += Math.abs(periods[i] - periods[i + 1]);
  const meanAbs = absDiff / (periods.length - 1);
  const meanPeriod = periods.reduce((a, b) => a + b, 0) / periods.length;
  return meanPeriod === 0 ? 0 : (meanAbs / meanPeriod) * 100;
}

/**
 * Local shimmer — same construction over peak amplitudes rather than periods.
 *
 * CAUTION: 19.3% of healthy men and 37.4% of healthy women exceed the MDVP
 * 3.810% "pathology" threshold when measured with Praat's algorithm. We never
 * display that threshold, and never call a value abnormal.
 */
export function shimmerLocal(amplitudes: number[]): number {
  if (amplitudes.length < 2) return 0;
  let absDiff = 0;
  for (let i = 0; i < amplitudes.length - 1; i++) {
    absDiff += Math.abs(amplitudes[i] - amplitudes[i + 1]);
  }
  const meanAbs = absDiff / (amplitudes.length - 1);
  const meanAmp = amplitudes.reduce((a, b) => a + b, 0) / amplitudes.length;
  return meanAmp === 0 ? 0 : (meanAbs / meanAmp) * 100;
}

/**
 * Harmonics-to-noise ratio, in dB, via the autocorrelation method.
 *
 *   HNR = 10 · log10( r(T) / (1 − r(T)) )
 *
 * where r(T) is the normalised autocorrelation at the fundamental period.
 */
export function harmonicsToNoise(frame: Float32Array, sampleRate: number, f0: number): number {
  if (f0 <= 0) return 0;
  const tau = Math.round(sampleRate / f0);
  if (tau <= 0 || tau >= frame.length) return 0;

  let mean = 0;
  for (let i = 0; i < frame.length; i++) mean += frame[i];
  mean /= frame.length;

  let r0 = 0;
  let rt = 0;
  for (let i = 0; i < frame.length - tau; i++) {
    const a = frame[i] - mean;
    const b = frame[i + tau] - mean;
    r0 += a * a;
    rt += a * b;
  }
  if (r0 === 0) return 0;
  const r = Math.max(-0.999, Math.min(0.999, rt / r0));
  if (r <= 0) return 0;
  return 10 * Math.log10(r / (1 - r));
}

/* ------------------------------------------------------------------ *
 * Cepstral peak prominence
 * ------------------------------------------------------------------ */

/**
 * Hann window. Applied to the time-domain frame before spectral analysis.
 *
 * Omitting this was a real bug during development: without it, spectral
 * leakage moved the cepstral peak off the true quefrency and CPPS stopped
 * discriminating periodic from aperiodic sound entirely (8.45 vs 8.61 dB —
 * indistinguishable). With it, the same comparison separates by >16 dB.
 *
 * Note it is applied ONLY to the time-domain signal. Windowing the log
 * spectrum as well — a tempting symmetry — puts the peak an octave out.
 */
export function hannWindow(frame: Float32Array): Float32Array {
  const n = frame.length;
  const out = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    out[i] = frame[i] * (0.5 - 0.5 * Math.cos((2 * Math.PI * i) / (n - 1)));
  }
  return out;
}

/** Naive DFT magnitude spectrum. Only used on short cepstral windows. */
function magnitudeSpectrum(frame: Float32Array): Float32Array {
  const n = frame.length;
  const half = n >> 1;
  const mag = new Float32Array(half);
  for (let k = 0; k < half; k++) {
    let re = 0;
    let im = 0;
    for (let t = 0; t < n; t++) {
      const angle = (-2 * Math.PI * k * t) / n;
      re += frame[t] * Math.cos(angle);
      im += frame[t] * Math.sin(angle);
    }
    mag[k] = Math.sqrt(re * re + im * im);
  }
  return mag;
}

/**
 * Smoothed cepstral peak prominence (CPPS), in dB.
 *
 * The cepstral peak's height above the regression line fitted through the
 * cepstrum. Of the four measures here this has the best test-retest
 * reliability, which is why it is weighted most heavily once a personal
 * baseline exists.
 */
export function cepstralPeakProminence(
  frame: Float32Array,
  sampleRate: number,
  minHz = 60,
  maxHz = 350,
): number {
  // Window the time-domain frame — see hannWindow() for why this matters.
  const spectrum = magnitudeSpectrum(hannWindow(frame));
  const logSpec = new Float32Array(spectrum.length);
  for (let i = 0; i < spectrum.length; i++) {
    logSpec[i] = 20 * Math.log10(Math.max(spectrum[i], 1e-10));
  }

  // No window here — the log spectrum is not a time-domain signal.
  const cepstrum = magnitudeSpectrum(logSpec);

  const qMin = Math.max(1, Math.floor(sampleRate / maxHz / 2));
  const qMax = Math.min(cepstrum.length - 1, Math.ceil(sampleRate / minHz / 2));
  if (qMax <= qMin) return 0;

  // Peak in the quefrency band of interest
  let peakIdx = qMin;
  for (let q = qMin; q <= qMax; q++) if (cepstrum[q] > cepstrum[peakIdx]) peakIdx = q;

  // Least-squares regression across the band, in dB
  let sx = 0;
  let sy = 0;
  let sxy = 0;
  let sxx = 0;
  let n = 0;
  for (let q = qMin; q <= qMax; q++) {
    const y = 20 * Math.log10(Math.max(cepstrum[q], 1e-10));
    sx += q;
    sy += y;
    sxy += q * y;
    sxx += q * q;
    n++;
  }
  const denom = n * sxx - sx * sx;
  if (denom === 0) return 0;
  const slope = (n * sxy - sx * sy) / denom;
  const intercept = (sy - slope * sx) / n;

  const peakDb = 20 * Math.log10(Math.max(cepstrum[peakIdx], 1e-10));
  const regressionDb = slope * peakIdx + intercept;
  return peakDb - regressionDb;
}

/* ------------------------------------------------------------------ *
 * Statistics
 * ------------------------------------------------------------------ */

export function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = sorted.length >> 1;
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

export function iqr(values: number[]): number {
  if (values.length < 4) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const q = (p: number) => {
    const pos = (sorted.length - 1) * p;
    const lo = Math.floor(pos);
    const hi = Math.ceil(pos);
    return sorted[lo] + (sorted[hi] - sorted[lo]) * (pos - lo);
  };
  return q(0.75) - q(0.25);
}

export function mean(values: number[]): number {
  return values.length === 0 ? 0 : values.reduce((a, b) => a + b, 0) / values.length;
}

